import { PreQualAnswers, PreQualConfig, PreQualQuestion } from '../types/JobOpenings';
import { rtkApi } from './rtkApi';

export type PreQualAnswerPayload = {
  questionKey: string;
  value: string;
  location?: {
    city: string;
    state: string;
    country: string;
    postcode: string;
    lat: number;
    lng: number;
    unknown: boolean;
  };
  videoKey?: string;
};

export type PreQualVideoPresignResponse = {
  url: string;
  videoKey: string;
};

type PreQualVideoPresignRequest = {
  jobApplicantId?: string;
  questionKey: string;
  token?: string;
};

export const preQualApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    getPreQualConfig: builder.query<PreQualConfig, string>({
      query: (jobOpeningId) => `/pre-qual/config/${jobOpeningId}`,
      providesTags: (result, error, jobOpeningId) => [
        { type: 'PreQualConfig', id: jobOpeningId },
      ],
    }),
    updatePreQualQuestions: builder.mutation<
      PreQualConfig,
      { jobOpeningId: string; questions: PreQualQuestion[] }
    >({
      query: ({ jobOpeningId, questions }) => ({
        url: `/pre-qual/config/${jobOpeningId}/questions`,
        method: 'PUT',
        body: { questions },
      }),
      invalidatesTags: (result, error, { jobOpeningId }) => [
        { type: 'PreQualConfig', id: jobOpeningId },
      ],
    }),
    getCandidateAnswers: builder.query<
      PreQualAnswers,
      { jobOpeningId: string; jobApplicantId: string }
    >({
      query: ({ jobOpeningId, jobApplicantId }) =>
        `/pre-qual/answers/${jobOpeningId}/${jobApplicantId}`,
      providesTags: (result, error, { jobApplicantId }) => [
        { type: 'PreQualAnswer', id: jobApplicantId },
      ],
    }),
    resendPreQualInvite: builder.mutation<any, string>({
      query: (jobApplicantId) => ({
        url: `/pre-qual/resend/${jobApplicantId}`,
        method: 'POST',
      }),
    }),
    getPreQualForm: builder.query<
      { questions: PreQualQuestion[]; jobTitle: string; orgName: string },
      string
    >({
      query: (jobOpeningId) => ({
        url: `/pre-qual${jobOpeningId}/form`,
        method: 'GET',
      }),
    }),
    submitPreQual: builder.mutation<
      void,
      { token: string; answers: PreQualAnswerPayload[] }
    >({
      query: ({ token, answers }) => ({
        url: '/pre-qual/submit',
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: { answers },
      }),
    }),
    submitPublicPreQual: builder.mutation<
      void,
      { jobApplicantId: string; answers: PreQualAnswerPayload[] }
    >({
      query: ({ jobApplicantId, answers }) => ({
        url: `/pre-qual/submit-public/${jobApplicantId}`,
        method: 'POST',
        body: { answers },
      }),
    }),
    getPublicPreQualVideoPresign: builder.mutation<
      PreQualVideoPresignResponse,
      PreQualVideoPresignRequest
    >({
      query: ({ jobApplicantId, questionKey, token }) => {
        const params = new URLSearchParams({
          question_key: questionKey,
        });
        if (jobApplicantId) {
          params.set('job_applicant_id', jobApplicantId);
        }

        return {
          url: `/pre-qual/video-presign?${params.toString()}`,
          method: 'POST',
          headers: token
            ? {
              Authorization: `Bearer ${token}`,
            }
            : undefined,
        };
      },
      transformResponse: (
        response:
          | string
          | {
              url?: string;
              uploadUrl?: string;
              upload_link?: string;
              videoKey?: string;
              video_key?: string;
            },
      ): PreQualVideoPresignResponse => {
        if (typeof response === 'string') {
          return { url: response, videoKey: '' };
        }
        return {
          url: response.url || response.uploadUrl || response.upload_link || '',
          videoKey: response.videoKey || response.video_key || '',
        };
      },
    }),
    getPreQualVideoPresign: builder.mutation<
      PreQualVideoPresignResponse,
      PreQualVideoPresignRequest
    >({
      query: ({ questionKey, jobApplicantId, token }) => {
        const params = new URLSearchParams({
          question_key: questionKey,
        });
        if (jobApplicantId) {
          params.set('job_applicant_id', jobApplicantId);
        }

        return {
          url: `/pre-qual/video-presign?${params.toString()}`,
          method: 'POST',
          headers: token
            ? {
              Authorization: `Bearer ${token}`,
            }
            : undefined,
        };
      },
      transformResponse: (
        response:
          | string
          | {
              url?: string;
              uploadUrl?: string;
              upload_link?: string;
              videoKey?: string;
              video_key?: string;
            },
      ): PreQualVideoPresignResponse => {
        if (typeof response === 'string') {
          return { url: response, videoKey: '' };
        }

        return {
          url: response.url || response.uploadUrl || response.upload_link || '',
          videoKey: response.videoKey || response.video_key || '',
        };
      },
    }),
  }),
});

export const {
  useGetPreQualConfigQuery,
  useLazyGetPreQualConfigQuery,
  useUpdatePreQualQuestionsMutation,
  useGetCandidateAnswersQuery,
  useResendPreQualInviteMutation,
  useGetPreQualFormQuery,
  useLazyGetPreQualFormQuery,
  useSubmitPreQualMutation,
  useSubmitPublicPreQualMutation,
  useGetPreQualVideoPresignMutation,
  useGetPublicPreQualVideoPresignMutation,
} = preQualApi;
